import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Mail, Shield, Eye, Users, Database, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import './PrivacyPolicy.css';

function PrivacyPolicy() {
  const [expandedSections, setExpandedSections] = useState({});
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const lastUpdated = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const ExpandableSection = ({ id, title, icon: Icon, children }) => {
    const isExpanded = expandedSections[id];
    return (
      <div className={`privacy-section${isExpanded ? " expanded" : ""}`}>
        <button
          onClick={() => toggleSection(id)}
          className="privacy-section-header"
          aria-expanded={isExpanded}
        >
          <div className="privacy-section-header-content">
            <span className="privacy-section-icon"><Icon /></span>
            <span className="privacy-section-title">{title}</span>
          </div>
          <span className="privacy-section-chevron">
            {isExpanded ? <ChevronDown /> : <ChevronRight />}
          </span>
        </button>
        {isExpanded && (
          <div className="privacy-section-content">
            {children}
          </div>
        )}
      </div>
    );
  };

  return (
    
    <section className="privacy-policy">
      <div className="privacy-container">
        {/* Header */}
        <div className="privacy-header">
          <div className="privacy-header-icon">
            <Shield />
          </div>
          <h1 className="privacy-title">Privacy Policy</h1>
          <div className="privacy-subtitle">
            We are committed to protecting your privacy and ensuring the security of your personal information. This policy explains how we collect, use, and safeguard your data.
          </div>
         <div className="privacy-update-badge">
  Last Updated: {lastUpdated}
</div>



<Link to="/" className="privacy-update-badge" style={{ marginLeft: '40px' }}>
  Homepage
</Link>
        </div>

        {/* Quick Summary */}
        <div className="privacy-summary">
          <div className="privacy-summary-title">Privacy at a Glance</div>
          <div className="privacy-summary-grid">
            <div className="privacy-summary-item">
              <span className="privacy-summary-icon icon-eye"><Eye /></span>
              <span className="privacy-summary-text">We collect only necessary information for our services</span>
            </div>
            <div className="privacy-summary-item">
              <span className="privacy-summary-icon icon-users"><Users /></span>
              <span className="privacy-summary-text">Your data is never sold to third parties</span>
            </div>
            <div className="privacy-summary-item">
              <span className="privacy-summary-icon icon-lock"><Lock /></span>
              <span className="privacy-summary-text">Industry-standard security measures protect your information</span>
            </div>
          </div>
        </div>

        {/* Expandable Sections */}
        <ExpandableSection id="controller" title="Data Controller Information" icon={Database}>
          <div className="privacy-content-text">
            Byfotech ("we," "our," or "us") is the data controller responsible for your personal information. We are a technology company specializing in web development, mobile applications, and digital solutions.
          </div>
          <div className="privacy-content-card">
            <div className="privacy-content-card-title">Contact Information:</div>
            <ul className="privacy-content-list">
              <li><strong>Email:</strong> byfotech@gmail.com</li>
              <li><strong>Data Protection Officer:</strong> privacy@byfotech.com</li>
              <li><strong>Response Time:</strong> We respond to privacy inquiries within 72 hours</li>
            </ul>
          </div>
        </ExpandableSection>

        <ExpandableSection id="collection" title="Information We Collect" icon={Database}>
          <div className="privacy-content-grid">
            <div className="privacy-content-card">
              <div className="privacy-content-card-title">Personal Information You Provide:</div>
              <ul className="privacy-content-list">
                <li>Full name and title</li>
                <li>Email address</li>
                <li>Phone number</li>
                <li>Company name and position</li>
                <li>Mailing address (if required)</li>
              </ul>
            </div>
            <div className="privacy-content-card">
              <div className="privacy-content-card-title">Project Information</div>
              <ul className="privacy-content-list">
                <li>Project requirements and specifications</li>
                <li>Budget and timeline preferences</li>
                <li>Communication preferences</li>
                <li>Files and documents shared</li>
                <li>Meeting notes and recordings</li>
              </ul>
            </div>
          </div>
          <div className="privacy-content-card">
            <div className="privacy-content-card-title">Information Collected Automatically:</div>
            <ul className="privacy-content-list">
              <li>IP address, browser type, device information, pages visited, time spent on pages, referral sources, and geographic location (for analytics)</li>
              <li>Session cookies for functionality, analytics cookies for performance measurement, and preference cookies for user experience enhancement</li>
              <li>Email interactions, support ticket history, chat logs, and response patterns to improve our services</li>
            </ul>
          </div>
        </ExpandableSection>

        <ExpandableSection id="usage" title="How We Use Your Information" icon={Eye}>
          <div className="privacy-content-grid">
            <div className="privacy-content-card">
              <div className="privacy-content-card-title">Primary Uses:</div>
              <ul className="privacy-content-list">
                <li>Respond to inquiries and provide requested services</li>
                <li>Process and fulfill project requirements</li>
                <li>Communicate project updates and milestones</li>
                <li>Provide customer support and technical assistance</li>
              </ul>
            </div>
            <div className="privacy-content-card">
              <div className="privacy-content-card-title">Secondary Uses:</div>
              <ul className="privacy-content-list">
                <li>Improve our website functionality and user experience</li>
                <li>Analyze usage patterns and performance metrics</li>
                <li>Send relevant updates and offers (with consent)</li>
                <li>Comply with legal obligations and prevent fraud</li>
              </ul>
            </div>
          </div>
          <div className="privacy-highlight-box">
            <div className="privacy-highlight-title">Legal Basis for Processing (GDPR)</div>
            <div className="privacy-highlight-text">
              We process your data based on: (1) Legitimate business interests, (2) Contract performance, (3) Your explicit consent, and (4) Legal compliance requirements.
            </div>
          </div>
        </ExpandableSection>

        <ExpandableSection id="sharing" title="Data Sharing and Third Parties" icon={Users}>
          <div className="privacy-highlight-box">
            <div className="privacy-highlight-title">We Never Sell Your Data</div>
            <div className="privacy-highlight-text">
              Your personal information is never sold, rented, or traded to third parties for marketing purposes.
            </div>
          </div>
          <div className="privacy-content-card">
            <div className="privacy-content-card-title">Limited Sharing Scenarios:</div>
            <ul className="privacy-content-list">
              <li>
                <strong>Service Providers:</strong> Trusted partners who assist with hosting, analytics, payment processing, and email services. All providers are contractually bound to protect your data.
              </li>
              <li>
                <strong>Legal Requirements:</strong> We may disclose information when required by law, court order, or to protect our rights, property, or safety, or that of others.
              </li>
              <li>
                <strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred to the new entity with the same privacy protections.
              </li>
            </ul>
          </div>
        </ExpandableSection>

        <ExpandableSection id="security" title="Data Security Measures" icon={Lock}>
          <div className="privacy-content-grid">
            <div className="privacy-content-card">
              <div className="privacy-content-card-title">Technical Safeguards</div>
              <ul className="privacy-content-list">
                <li>SSL/TLS encryption for data transmission</li>
                <li>AES-256 encryption for data at rest</li>
                <li>Regular security audits and penetration testing</li>
                <li>Secure cloud infrastructure with AWS/Azure</li>
                <li>Two-factor authentication for admin access</li>
              </ul>
            </div>
            <div className="privacy-content-card">
              <div className="privacy-content-card-title">Administrative Safeguards</div>
              <ul className="privacy-content-list">
                <li>Limited access on a need-to-know basis</li>
                <li>Regular staff training on data protection</li>
                <li>Background checks for personnel</li>
                <li>Incident response and breach notification procedures</li>
                <li>Regular backup and disaster recovery testing</li>
              </ul>
            </div>
          </div>
          <div className="privacy-highlight-box">
            <div className="privacy-highlight-title">Data Breach Response</div>
            <div className="privacy-highlight-text">
              In the unlikely event of a data breach, we will notify affected users within 72 hours and provide details about the incident, potential impact, and remedial actions taken.
            </div>
          </div>
        </ExpandableSection>

        <ExpandableSection id="rights" title="Your Privacy Rights" icon={Shield}>
          <div className="privacy-content-grid">
            <div className="privacy-content-card">
              <div className="privacy-content-card-title">Access and Portability</div>
              <div className="privacy-content-text">
                Request a copy of your personal data in a structured, commonly used format. We'll provide this information within 30 days of your request.
              </div>
            </div>
            <div className="privacy-content-card">
              <div className="privacy-content-card-title">Rectification and Updates</div>
              <div className="privacy-content-text">
                Correct or update any inaccurate or incomplete personal information we hold about you. You can update most information through your account settings.
              </div>
            </div>
            <div className="privacy-content-card">
              <div className="privacy-content-card-title">Erasure and Deletion</div>
              <div className="privacy-content-text">
                Request deletion of your personal data when it's no longer necessary for the purposes for which it was collected, subject to legal retention requirements.
              </div>
            </div>
            <div className="privacy-content-card">
              <div className="privacy-content-card-title">Consent Withdrawal and Objection</div>
              <div className="privacy-content-text">
                Withdraw consent for data processing or object to processing based on legitimate interests. This may affect our ability to provide certain services.
              </div>
            </div>
          </div>
          <div className="privacy-highlight-box">
            <div className="privacy-highlight-title">How to Exercise Your Rights</div>
            <div className="privacy-highlight-text">
              To exercise any of these rights, contact us at byfotech@gmail.com with your request. Please include sufficient information to verify your identity. Response time: Within 30 days for most requests.
            </div>
          </div>
        </ExpandableSection>

        <ExpandableSection id="retention" title="Data Retention" icon={Database}>
          <div className="privacy-content-card">
            <div className="privacy-content-card-title">Retention Periods</div>
            <ul className="privacy-content-list">
              <li>Contact Information: 3 years after last contact</li>
              <li>Project Files and Communications: 7 years for business records</li>
              <li>Website Analytics Data: 26 months (anonymized)</li>
              <li>Marketing Communications: Until unsubscribe</li>
            </ul>
          </div>
          <div className="privacy-highlight-box">
            <div className="privacy-highlight-title">Secure Deletion</div>
            <div className="privacy-highlight-text">
              When data reaches the end of its retention period, it is securely deleted using industry-standard methods to ensure it cannot be recovered.
            </div>
          </div>
        </ExpandableSection>

        <ExpandableSection id="international" title="International Data Transfers" icon={Database}>
          <div className="privacy-content-grid">
            <div className="privacy-content-card">
              <div className="privacy-content-card-title">EU-US Data Transfers</div>
              <div className="privacy-content-text">
                We use Standard Contractual Clauses (SCCs) approved by the European Commission for transfers to the United States and other third countries.
              </div>
            </div>
            <div className="privacy-content-card">
              <div className="privacy-content-card-title">Adequacy Decisions</div>
              <div className="privacy-content-text">
                Where possible, we transfer data to countries with adequacy decisions from the European Commission, ensuring equivalent data protection standards.
              </div>
            </div>
          </div>
        </ExpandableSection>

        <ExpandableSection id="cookies" title="Cookie Policy" icon={Eye}>
          <div className="privacy-content-grid">
            <div className="privacy-content-card">
              <div className="privacy-content-card-title">Essential Cookies <span style={{color: "#059669", fontWeight: 500, fontSize: "0.95em"}}>Required</span></div>
              <div className="privacy-content-text">
                Necessary for basic website functionality, security, and user authentication. Cannot be disabled without affecting site operation.
              </div>
            </div>
            <div className="privacy-content-card">
              <div className="privacy-content-card-title">Analytics Cookies <span style={{color: "#2563eb", fontWeight: 500, fontSize: "0.95em"}}>Optional</span></div>
              <div className="privacy-content-text">
                Help us understand how visitors interact with our website by collecting and reporting information anonymously.
              </div>
            </div>
            <div className="privacy-content-card">
              <div className="privacy-content-card-title">Preference Cookies <span style={{color: "#7c3aed", fontWeight: 500, fontSize: "0.95em"}}>Optional</span></div>
              <div className="privacy-content-text">
                Remember your preferences and choices to provide a more personalized experience on subsequent visits.
              </div>
            </div>
          </div>
          <div className="privacy-highlight-box">
            <div className="privacy-highlight-title">Cookie Management</div>
            <div className="privacy-highlight-text">
              You can control cookies through your browser settings or our cookie consent banner. Note that disabling certain cookies may affect website functionality.
            </div>
          </div>
        </ExpandableSection>

        <ExpandableSection id="children" title="Children's Privacy" icon={Shield}>
          <div className="privacy-highlight-box">
            <div className="privacy-highlight-title">Age Restrictions</div>
            <div className="privacy-highlight-text">
              Our services are not intended for children under 16 years of age. We do not knowingly collect personal information from children under 16.
            </div>
          </div>
          <div className="privacy-content-text">
            If we become aware that we have collected personal information from a child under 16 without parental consent, we will take steps to remove that information from our systems promptly.
          </div>
          <div className="privacy-highlight-box">
            <div className="privacy-highlight-title">Parental Rights</div>
            <div className="privacy-highlight-text">
              Parents or guardians who believe we may have collected information from their child should contact us immediately at byfotech@gmail.com for prompt removal of such information.
            </div>
          </div>
        </ExpandableSection>

        <ExpandableSection id="updates" title="Policy Updates" icon={Database}>
          <div className="privacy-content-text">
            We may update this privacy policy periodically to reflect changes in our practices, legal requirements, or business operations.
          </div>
          <div className="privacy-content-grid">
            <div className="privacy-content-card">
              <div className="privacy-content-card-title">Notification of Changes</div>
              <div className="privacy-content-text">
                Material changes will be communicated through email notifications, website banners, or direct communication, depending on the nature of the changes.
              </div>
            </div>
            <div className="privacy-content-card">
              <div className="privacy-content-card-title">Version History</div>
              <div className="privacy-content-text">
                We maintain a record of significant policy changes and their effective dates. Previous versions are available upon request.
              </div>
            </div>
          </div>
          <div className="privacy-content-text" style={{fontSize: "0.95em"}}>
            Continued use of our services after policy updates constitutes acceptance of the revised terms.
          </div>
        </ExpandableSection>

        {/* Contact Section */}
        <div className="privacy-contact">
          <div className="privacy-contact-title">Questions About Your Privacy?</div>
          <div className="privacy-contact-subtitle">
            We're committed to transparency and are here to help with any privacy-related questions or concerns. Our team typically responds within 24-48 hours.
          </div>
          <div className="privacy-contact-buttons">
            <Link to="/#booking" className="privacy-contact-button">
              <Mail /> byfotech@gmail.com</Link>
            
            <Link to="/#contact" className="privacy-contact-button">
              <Shield /> Contact Us Directly </Link>
           

          </div>
        </div>
      </div>
    </section>
  );
}

export default PrivacyPolicy;